import * as React from 'react'
import clx from 'classnames'
import { createPortal } from 'react-dom'
import { FaIcon, faBellRegular, faXmark } from '@/lib/fa'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useNewsFeed } from '@/modules/shared/hooks/useNewsFeed'
import { NewsEntry } from './NewsEntry'

type Props = {
  collapsed?: boolean
}

const PANEL_WIDTH = 360
const VIEWPORT_MARGIN = 12

export const NewsBell: React.FC<Props> = ({ collapsed }) => {
  const { tSafe } = useLocalize()
  const { items, unseenCount, isUnseen, markAllSeen } = useNewsFeed()

  const [open, setOpen] = React.useState(false)
  const [position, setPosition] = React.useState<React.CSSProperties | null>(null)
  const triggerRef = React.useRef<HTMLDivElement>(null)
  const panelRef = React.useRef<HTMLDivElement>(null)

  const close = React.useCallback(() => {
    setOpen(false)
    markAllSeen()
  }, [markAllSeen])

  const updatePosition = React.useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) return

    const left = Math.min(
      rect.right + 10,
      window.innerWidth - PANEL_WIDTH - VIEWPORT_MARGIN,
    )
    const bottom = Math.max(
      VIEWPORT_MARGIN,
      window.innerHeight - rect.bottom - 4,
    )

    setPosition({
      position: 'fixed',
      left: Math.max(VIEWPORT_MARGIN, left),
      bottom,
      width: PANEL_WIDTH,
      maxWidth: `calc(100vw - ${VIEWPORT_MARGIN * 2}px)`,
      zIndex: 300,
    })
  }, [])

  React.useEffect(() => {
    if (!open) return undefined
    updatePosition()

    const onViewportChange = () => updatePosition()
    window.addEventListener('resize', onViewportChange)
    window.addEventListener('scroll', onViewportChange, true)
    return () => {
      window.removeEventListener('resize', onViewportChange)
      window.removeEventListener('scroll', onViewportChange, true)
    }
  }, [open, updatePosition])

  React.useEffect(() => {
    if (!open) return undefined

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (
        panelRef.current?.contains(target) ||
        triggerRef.current?.contains(target)
      )
        return
      close()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, close])

  const label = tSafe('news.bell-tooltip', 'Nowości')

  return (
    <>
      <div ref={triggerRef} className="tw-relative tw-flex">
        <button
          type="button"
          aria-label={label}
          aria-expanded={open}
          onClick={() => (open ? close() : setOpen(true))}
          className={clx(
            'tw-relative tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded tw-transition-colors',
            open ? 'tw-bg-primary' : 'tw-bg-base-100 hover:tw-bg-base-200',
          )}
        >
          <FaIcon
            icon={faBellRegular}
            className={clx(
              'tw-h-4 tw-w-4',
              open ? 'tw-text-white' : 'tw-text-content-base tw-opacity-80',
            )}
          />
          {unseenCount > 0 && (
            <span
              className={clx(
                'tw-absolute tw-right-0.5 tw-top-0.5 tw-flex tw-h-4 tw-min-w-[16px] tw-items-center tw-justify-center',
                'tw-rounded-full tw-bg-error tw-px-1 tw-text-[10px] tw-font-bold tw-leading-none tw-text-white',
              )}
            >
              {unseenCount > 9 ? '9+' : unseenCount}
            </span>
          )}
        </button>
        {!collapsed && (
          <button
            type="button"
            onClick={() => (open ? close() : setOpen(true))}
            className="tw-ml-2 tw-hidden tw-items-center tw-text-xs tw-text-content-base tw-opacity-70 tw-transition-opacity hover:tw-opacity-100 md:tw-flex"
          >
            {label}
          </button>
        )}
      </div>

      {open &&
        position &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label={label}
            style={position}
            className="tw-flex tw-max-h-[70vh] tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-base-300 tw-bg-base-100 tw-shadow-multi"
          >
            <div className="tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-base-300 tw-px-4 tw-py-3">
              <FaIcon
                icon={faBellRegular}
                className="tw-h-3.5 tw-w-3.5 tw-text-primary"
              />
              <h2 className="tw-text-sm tw-font-semibold tw-text-content-base">
                {tSafe('news.title', 'Nowości')}
              </h2>
              {unseenCount > 0 && (
                <span className="tw-rounded-full tw-bg-primary/10 tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-text-primary">
                  {unseenCount} {tSafe('news.badge-new', 'Nowe').toLowerCase()}
                </span>
              )}
              <button
                type="button"
                onClick={close}
                aria-label={tSafe('news.close', 'Zamknij')}
                className="tw-ml-auto tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded tw-text-content-base tw-opacity-60 tw-transition-opacity hover:tw-opacity-100"
              >
                <FaIcon icon={faXmark} className="tw-h-3.5 tw-w-3.5" />
              </button>
            </div>

            <div className="custom-scrollbar tw-flex tw-flex-col tw-gap-2 tw-overflow-y-auto tw-px-3 tw-py-3">
              {items.length === 0 ? (
                <p className="tw-px-1 tw-py-6 tw-text-center tw-text-xs tw-text-neutral">
                  {tSafe('news.empty', 'Brak nowości')}
                </p>
              ) : (
                items.map((item) => (
                  <NewsEntry
                    key={item.id}
                    item={item}
                    variant="compact"
                    highlighted={isUnseen(item)}
                  />
                ))
              )}
            </div>

          </div>,
          document.body,
        )}
    </>
  )
}
